import type { CustomAttributes, VisibilityRules } from "@emkit/builder-core";
import { isValidAnchor } from "@emkit/builder-core";
import { TextField } from "./fields.js";

interface AdvancedFieldsProps {
  value: CustomAttributes;
  onChange: (patch: Partial<CustomAttributes>) => void;
}

/**
 * Shared "Advanced" panel rendered at the bottom of every widget, section
 * and column inspector. Surfaces the four CustomAttributes fields that
 * otherwise are only editable at the JSON level: anchor (deep-link id),
 * className (extra CSS classes), style (raw inline CSS), hideOn (per
 * breakpoint visibility toggle).
 *
 * Wrapped in a native <details> so it stays out of the way for routine
 * edits — these fields are intentionally power-user territory.
 */
export function AdvancedFields({ value, onChange }: AdvancedFieldsProps) {
  const anchor = value.anchor ?? "";
  const anchorInvalid = anchor.length > 0 && !isValidAnchor(anchor);
  const hideOn: VisibilityRules = value.hideOn ?? {};

  const setHideOn = (bp: keyof VisibilityRules, on: boolean) => {
    const next: VisibilityRules = { ...hideOn };
    if (on) {
      next[bp] = true;
    } else {
      delete next[bp];
    }
    // Drop the hideOn key entirely when no flags remain — keeps the JSON tidy
    // and prevents { hideOn: {} } from sticking around forever in the saved page.
    onChange({ hideOn: Object.keys(next).length === 0 ? undefined : next });
  };

  return (
    <details className="emke-advanced">
      <summary>Advanced</summary>
      <div className="emke-advanced-body">
        <TextField
          label="Anchor (URL fragment)"
          value={anchor}
          placeholder="pricing"
          onChange={(v) => onChange({ anchor: v || undefined })}
        />
        {anchorInvalid && (
          <div className="emke-field-error">
            Anchor must start with a letter; allowed: A-Z a-z 0-9 _ -
          </div>
        )}

        <div className="emke-inspector-field">
          <label>Hide on</label>
          <div className="emke-checkbox-row">
            <label className="emke-checkbox-item">
              <input
                type="checkbox"
                checked={!!hideOn.mobile}
                onChange={(e) => setHideOn("mobile", e.target.checked)}
              />
              Mobile
            </label>
            <label className="emke-checkbox-item">
              <input
                type="checkbox"
                checked={!!hideOn.tablet}
                onChange={(e) => setHideOn("tablet", e.target.checked)}
              />
              Tablet
            </label>
            <label className="emke-checkbox-item">
              <input
                type="checkbox"
                checked={!!hideOn.desktop}
                onChange={(e) => setHideOn("desktop", e.target.checked)}
              />
              Desktop
            </label>
          </div>
        </div>

        <TextField
          label="CSS class"
          value={value.className ?? ""}
          placeholder="my-class another-class"
          onChange={(v) => onChange({ className: v || undefined })}
        />
        <TextField
          label="Inline CSS"
          value={value.style ?? ""}
          placeholder="z-index:10; box-shadow:..."
          multiline
          onChange={(v) => onChange({ style: v || undefined })}
        />
      </div>
    </details>
  );
}
